// Display mirror of the framework catalogue in backend lib/frameworks.ts: what kind of thing each
// enrolment is and how the rest of the platform treats it. Every framework category set the pages
// use (context/FrameworksContext.tsx re-exports them) derives from this one table, and the backend's
// frameworkCatalogueSync.test.ts fails when the two disagree. Pure: no React, no API.

export type FrameworkId =
  | "iso27001"
  | "cyber_essentials"
  | "cyber_essentials_plus"
  | "iso42001"
  | "iso9001"
  | "soc2"
  | "pci_dss"
  | "iasme_ca_sole_trader"
  | "iasme_ca_micro"
  | "iasme_ca_small"
  | "iasme_ca_medium_large"
  | "nist_csf"
  | "gdpr"
  | "uk_gdpr"
  | "iso45001"
  | "iso14001"
  | "iwa48"
  | "ncsc_caf";

export type FrameworkKind = "standard" | "scheme" | "maturity" | "regulation";
export type FrameworkFee = "standard" | "free_reference" | "regulation" | "esg_addon";

export interface FrameworkSpec {
  /** The short name the pages show (the backend's label is longer). */
  shortLabel: string;
  kind: FrameworkKind;
  fee: FrameworkFee;
  certifiable: boolean;
  aiAssessable: boolean;
  scoreable: boolean;
  /** Whose requirement rows it is assessed against (Cyber Essentials Plus → Cyber Essentials). */
  requirementsOf?: FrameworkId;
  alternativeGroup?: "cyber_essentials" | "iasme_ca";
}

const STANDARD = { kind: "standard", fee: "standard", certifiable: true, aiAssessable: true, scoreable: true } as const;
const IASME = { kind: "scheme", fee: "standard", certifiable: true, aiAssessable: true, scoreable: true, alternativeGroup: "iasme_ca" } as const;
const REGULATION = { kind: "regulation", fee: "regulation", certifiable: false, aiAssessable: false, scoreable: false } as const;

/** In display order. */
export const FRAMEWORK_CATALOGUE: Readonly<Record<FrameworkId, FrameworkSpec>> = {
  iso27001:              { ...STANDARD, shortLabel: "ISO 27001" },
  cyber_essentials:      { kind: "scheme", fee: "standard", certifiable: true, aiAssessable: true, scoreable: true, alternativeGroup: "cyber_essentials", shortLabel: "Cyber Essentials" },
  cyber_essentials_plus: { kind: "scheme", fee: "standard", certifiable: true, aiAssessable: true, scoreable: true, alternativeGroup: "cyber_essentials", requirementsOf: "cyber_essentials", shortLabel: "Cyber Essentials Plus" },
  iso42001:              { ...STANDARD, shortLabel: "ISO 42001" },
  iso9001:               { ...STANDARD, shortLabel: "ISO 9001" },
  soc2:                  { ...STANDARD, shortLabel: "SOC 2" },
  pci_dss:               { ...STANDARD, shortLabel: "PCI DSS v4.0" },
  iasme_ca_sole_trader:  { ...IASME, shortLabel: "IASME CA (Sole Trader)" },
  iasme_ca_micro:        { ...IASME, shortLabel: "IASME CA (Micro)" },
  iasme_ca_small:        { ...IASME, shortLabel: "IASME CA (Small)" },
  iasme_ca_medium_large: { ...IASME, shortLabel: "IASME CA (Medium/Large)" },
  nist_csf:              { kind: "maturity", fee: "free_reference", certifiable: false, aiAssessable: false, scoreable: false, shortLabel: "NIST CSF 2.0" },
  gdpr:                  { ...REGULATION, shortLabel: "GDPR" },
  uk_gdpr:               { ...REGULATION, shortLabel: "UK GDPR" },
  iso45001:              { ...STANDARD, shortLabel: "ISO 45001" },
  iso14001:              { ...STANDARD, fee: "esg_addon", shortLabel: "ISO 14001" },
  iwa48:                 { kind: "maturity", fee: "esg_addon", certifiable: false, aiAssessable: false, scoreable: false, shortLabel: "IWA 48:2024" },
  ncsc_caf:              { ...STANDARD, shortLabel: "NCSC CAF v4.0" },
};

const ENTRIES = Object.entries(FRAMEWORK_CATALOGUE) as Array<[FrameworkId, FrameworkSpec]>;
const idsWhere = (test: (spec: FrameworkSpec) => boolean): FrameworkId[] => ENTRIES.filter(([, s]) => test(s)).map(([id]) => id);

export const FRAMEWORK_ORDER: FrameworkId[] = ENTRIES.map(([id]) => id);
export const FRAMEWORK_LABELS: Record<FrameworkId, string> = Object.fromEntries(ENTRIES.map(([id, s]) => [id, s.shortLabel])) as Record<FrameworkId, string>;

/** Frameworks the AI compliance check can assess. */
export const AI_ASSESSABLE_FRAMEWORKS: ReadonlySet<FrameworkId> = new Set(idsWhere((s) => s.aiAssessable));

/**
 * Standards a certification bureau can be accredited for and engaged to certify. The type names the
 * ones that are not, so a page can key a record by every certifiable standard; the catalogue sync
 * test checks it says what the `certifiable` flags say.
 */
export type CertifiableFrameworkId = Exclude<FrameworkId, "nist_csf" | "gdpr" | "uk_gdpr" | "iwa48">;
export const CERTIFIABLE_FRAMEWORKS = idsWhere((s) => s.certifiable) as readonly CertifiableFrameworkId[];

/** Reference frameworks: free, but only alongside a paid standard. */
export const FREE_REFERENCE_FRAMEWORKS: ReadonlySet<FrameworkId> = new Set(idsWhere((s) => s.fee === "free_reference"));

/** Enrolments with no framework fee of their own (data protection regulations and free reference frameworks). */
export const UNPAID_FRAMEWORK_IDS: ReadonlySet<string> = new Set<string>([...idsWhere((s) => s.fee === "regulation" || s.fee === "free_reference"), "jersey_dpjl"]);

/** The standards the Environment & Sustainability Governance add-on pays for and enrols. */
export const ESG_FRAMEWORK_IDS: ReadonlySet<FrameworkId> = new Set(idsWhere((s) => s.fee === "esg_addon"));

/** Enrolments with no scoreable requirement set: shown as "n/a", never 0%. */
export const UNSCOREABLE_FRAMEWORK_IDS: ReadonlySet<FrameworkId> = new Set(idsWhere((s) => !s.scoreable));

/** Cyber Essentials and Cyber Essentials Plus. */
export const CE_FRAMEWORK_IDS: ReadonlySet<string> = new Set<string>(idsWhere((s) => s.alternativeGroup === "cyber_essentials"));

/** Levels of one scheme: an organisation holds one of each group. */
export const ALTERNATIVE_FRAMEWORK_GROUPS: ReadonlyArray<readonly string[]> = [
  ...new Set(ENTRIES.map(([, s]) => s.alternativeGroup).filter((g): g is NonNullable<typeof g> => !!g)),
].map((group) => idsWhere((s) => s.alternativeGroup === group));

/** The framework whose requirement rows an enrolment is assessed against (CE+ → CE). */
export function requirementFrameworkId(id: string): string {
  return (FRAMEWORK_CATALOGUE as Record<string, FrameworkSpec>)[id]?.requirementsOf ?? id;
}

/** The enrolments plus the frameworks whose rows they are assessed against. */
export function withRequirementFrameworks(ids: Iterable<string>): Set<string> {
  const out = new Set<string>();
  for (const id of ids) { out.add(id); out.add(requirementFrameworkId(id)); }
  return out;
}
